/**
 * Shared range selector for the Berichte tabs (Phase 3, §6.3). Presets (Letzte
 * 6/12 Monate, Dieses Jahr, Letztes Jahr) plus a custom from/to month range;
 * emits a normalised `{from,to}` so equal ranges share one report cache entry.
 *
 * The custom month inputs keep a local draft and only propagate upward when
 * the range is valid (`JJJJ-MM` on both sides, from ≤ to) — an intermediate
 * from > to state or a cleared/free-text field (Firefox renders type="month"
 * as plain text) must never fire doomed 422 report queries; the last valid
 * range keeps driving the tabs while an inline hint explains the problem.
 */
import { useState } from 'react';
import { Select } from '../../components/ui/Select.tsx';
import { currentMonth, trailingRange } from '../../lib/hooks/useReports.ts';
import { isValidMonthRange, MONTH_RE } from './monthRange.ts';
import type { MonthRange } from '../../lib/queryKeys.ts';

type Preset = 'last6' | 'last12' | 'thisYear' | 'lastYear' | 'custom';

const PRESET_OPTIONS = [
  { value: 'last6', label: 'Letzte 6 Monate' },
  { value: 'last12', label: 'Letzte 12 Monate' },
  { value: 'thisYear', label: 'Dieses Jahr' },
  { value: 'lastYear', label: 'Letztes Jahr' },
  { value: 'custom', label: 'Benutzerdefiniert' },
];

/** The default range shown when a Berichte tab first opens (trailing 12 months). */
export function defaultRange(): MonthRange {
  return trailingRange(12);
}

function presetRange(preset: Exclude<Preset, 'custom'>): MonthRange {
  const now = currentMonth();
  const year = Number(now.slice(0, 4));
  switch (preset) {
    case 'last6':
      return trailingRange(6, now);
    case 'last12':
      return trailingRange(12, now);
    case 'thisYear':
      return { from: `${year}-01`, to: now };
    case 'lastYear':
      return { from: `${year - 1}-01`, to: `${year - 1}-12` };
  }
}

export interface RangeSelectProps {
  value: MonthRange;
  onChange: (range: MonthRange) => void;
}

export function RangeSelect({ value, onChange }: RangeSelectProps) {
  const [preset, setPreset] = useState<Preset>('last12');

  const handlePreset = (next: string) => {
    const p = next as Preset;
    setPreset(p);
    if (p !== 'custom') onChange(presetRange(p));
  };

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-slate-400 dark:text-slate-500">Zeitraum</span>
        <div className="w-52">
          <Select value={preset} onValueChange={handlePreset} options={PRESET_OPTIONS} />
        </div>
      </label>

      {preset === 'custom' ? <CustomMonthRange value={value} onChange={onChange} /> : null}
    </div>
  );
}

/**
 * The custom from/to month fields. Keeps a local draft and calls `onChange`
 * only for valid ranges; invalid drafts render an inline hint while the last
 * valid range keeps driving the report queries.
 */
export function CustomMonthRange({ value, onChange }: RangeSelectProps) {
  // Draft kept in sync with the committed value via the sanctioned "adjust
  // state during render" pattern (an external update re-seeds it, no effect).
  const [draft, setDraft] = useState<MonthRange>(value);
  const [syncedValue, setSyncedValue] = useState<MonthRange>(value);
  if (value.from !== syncedValue.from || value.to !== syncedValue.to) {
    setSyncedValue(value);
    setDraft(value);
  }

  const updateDraft = (patch: Partial<MonthRange>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    // Propagate only valid ranges — invalid drafts never reach the queries.
    if (isValidMonthRange(next)) onChange(next);
  };

  const malformed = !MONTH_RE.test(draft.from) || !MONTH_RE.test(draft.to);
  const rangeInvalid = !malformed && draft.from > draft.to;

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
      <div className="flex items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 dark:text-slate-500">Von</span>
          <input
            type="month"
            value={draft.from}
            max={MONTH_RE.test(draft.to) ? draft.to : undefined}
            aria-label="Von Monat"
            onChange={(e) => updateDraft({ from: e.target.value })}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 transition-colors duration-150 focus:border-sky-600 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-sky-500"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 dark:text-slate-500">Bis</span>
          <input
            type="month"
            value={draft.to}
            min={MONTH_RE.test(draft.from) ? draft.from : undefined}
            aria-label="Bis Monat"
            onChange={(e) => updateDraft({ to: e.target.value })}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 transition-colors duration-150 focus:border-sky-600 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-sky-500"
          />
        </label>
      </div>

      {malformed ? (
        <p className="pb-2 text-xs text-rose-600 dark:text-rose-400">
          Bitte beide Monate im Format JJJJ-MM wählen.
        </p>
      ) : rangeInvalid ? (
        <p className="pb-2 text-xs text-rose-600 dark:text-rose-400">
          „Von" muss vor „Bis" liegen.
        </p>
      ) : null}
    </div>
  );
}
